import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { WarningAltFilled } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  Checkbox,
  CircularLoadingButton,
  ConfirmationDialog,
  ConfirmationDialogAction,
  ConfirmationDialogCancel,
  ConfirmationDialogContent,
  ConfirmationDialogDescription,
  ConfirmationDialogFooter,
  ConfirmationDialogHeader,
  ConfirmationDialogOverlay,
  ConfirmationDialogPortal,
  ConfirmationDialogRoot,
  ConfirmationDialogTitle,
  Label,
} from '@mint-lib/tw-components';

import useDeleteFlowMutation from '../../api/mutations/useDeleteFlowMutation.js';
import { useDeleteFlowModalContext } from '../../atoms/deleteFlowModal.js';

const DeleteFlowModal = () => {
  const { t } = useTranslation('@myn/approval_v2');

  const [checked, setChecked] = useState(false);
  const [modalContext, setModalContext] = useDeleteFlowModalContext();
  const { deleteFlow, isPending } = useDeleteFlowMutation();

  const isSubmitButtonDisabled =
    !modalContext || isPending || (modalContext.inUse && !checked);

  const handleToggleCheckbox = () => {
    setChecked((prev) => !prev);
  };

  const handleClose = () => {
    setChecked(false);
    setModalContext(null);
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      return;
    }
    handleClose();
  };

  const handleSubmit = async () => {
    if (!modalContext) {
      handleClose();
      return;
    }

    try {
      await deleteFlow({
        flowId: modalContext.flowId,
      });

      enqueueSnackbar({
        title: t('Flow deleted successfully.'),
        variant: 'success',
        removable: true,
      });
      modalContext.onSuccess?.();
    } catch {
      enqueueSnackbar({
        title: t('Flow was not deleted.'),
        variant: 'error',
        removable: true,
      });
    }
  };

  return (
    <ConfirmationDialog
      open={modalContext?.isOpened}
      onOpenChange={handleOpenChange}
    >
      <ConfirmationDialogPortal>
        <ConfirmationDialogOverlay onClick={handleClose} />
        <ConfirmationDialogRoot>
          <ConfirmationDialogHeader onClose={handleClose}>
            <WarningAltFilled className="size-4 fill-error-main" />
            <ConfirmationDialogTitle>
              {t('Delete this approval flow?')}
            </ConfirmationDialogTitle>
          </ConfirmationDialogHeader>
          <ConfirmationDialogContent>
            <ConfirmationDialogDescription>
              {modalContext?.inUse ? (
                <>
                  <span className="block">
                    {t(
                      'Deleting this flow will immediately withdraw all pending approval requests.',
                    )}
                  </span>
                  <span className="block">
                    {t(
                      'All participants will be notified. This action is permanent.',
                    )}
                  </span>
                  <Label htmlFor="delete-flow-checkbox" className="mt-4">
                    <Checkbox
                      data-dd-action-name="Confirm removal of the flow"
                      id="delete-flow-checkbox"
                      onClick={handleToggleCheckbox}
                    />
                    {t('I understand I will not be able to recover it')}
                  </Label>
                </>
              ) : (
                <>
                  <span>{t('This action is permanent.')}</span>{' '}
                  <span>
                    {t('The flow will be deleted and cannot be recovered.')}
                  </span>
                </>
              )}
            </ConfirmationDialogDescription>
          </ConfirmationDialogContent>
          <ConfirmationDialogFooter>
            <ConfirmationDialogCancel
              onClick={handleClose}
              data-dd-action-name="Close the modal"
            />
            <ConfirmationDialogAction
              data-dd-action-name="Delete the flow"
              color="error"
              onClick={handleSubmit}
              disabled={isSubmitButtonDisabled}
            >
              {isPending && (
                <CircularLoadingButton size="small" variant="contained" />
              )}
              {t('Yes, delete')}
            </ConfirmationDialogAction>
          </ConfirmationDialogFooter>
        </ConfirmationDialogRoot>
      </ConfirmationDialogPortal>
    </ConfirmationDialog>
  );
};

export default DeleteFlowModal;
